import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, Users, Camera, Home, Share2, CheckCircle2, Film, Megaphone, Calendar, Plane, Palette, FileText, BookOpen, Globe, Monitor, ShoppingCart, Settings, Target, Sparkles, MessageCircle, BarChart3 } from "lucide-react";
import Header from "@/components/Header";
import PageBackground from "@/components/PageBackground";
import Footer from "@/components/Footer";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { getSectionBySlug, sections } from "@/data/leistungenSections";
import goodyear0 from "@/assets/goodyear-nuerburgring-0.jpg.asset.json";
import goodyear1 from "@/assets/goodyear-nuerburgring-1.jpg.asset.json";
import goodyear2 from "@/assets/goodyear-nuerburgring-2.jpg.asset.json";
import goodyear3 from "@/assets/goodyear-nuerburgring-3.jpg.asset.json";
import goodyear4 from "@/assets/goodyear-nuerburgring-4.jpg.asset.json";
import goodyear5 from "@/assets/goodyear-nuerburgring-5.jpg.asset.json";
import harpen1 from "@/assets/harpen-bochum-1.jpg.asset.json";
import harpen2 from "@/assets/harpen-bochum-2.jpg.asset.json";
import harpen4 from "@/assets/harpen-bochum-4.jpg.asset.json";
import harpen9 from "@/assets/harpen-bochum-9.jpg.asset.json";
import harpen10 from "@/assets/harpen-bochum-10.jpg.asset.json";
import angacom1 from "@/assets/angacom-zcn-1.jpg.asset.json";
import angacom8 from "@/assets/angacom-zcn-8.jpg.asset.json";
import angacom23 from "@/assets/angacom-zcn-23.jpg.asset.json";
import angacom26 from "@/assets/angacom-zcn-26.jpg.asset.json";
import porsche38 from "@/assets/porschecup-bg-38.jpg.asset.json";
import porsche56 from "@/assets/porschecup-bg-56.jpg.asset.json";
import porsche60 from "@/assets/porschecup-bg-60.jpg.asset.json";
import porsche63 from "@/assets/porschecup-bg-63.jpg.asset.json";
import porsche80 from "@/assets/porschecup-bg-80.jpg.asset.json";
import porsche83 from "@/assets/porschecup-bg-83.jpg.asset.json";
import porsche99 from "@/assets/porschecup-bg-99.jpg.asset.json";

const fotoBgImages = [
  { src: goodyear0.url, top: "8%", left: "4%", rotate: -8, w: "16rem" },
  { src: goodyear1.url, top: "55%", left: "2%", rotate: 6, w: "14rem" },
  { src: goodyear2.url, top: "12%", right: "5%", rotate: 9, w: "18rem" },
  { src: goodyear3.url, top: "62%", right: "3%", rotate: -7, w: "15rem" },
  { src: goodyear4.url, top: "78%", left: "38%", rotate: 4, w: "13rem" },
  { src: goodyear5.url, top: "2%", left: "42%", rotate: -5, w: "12rem" },
  { src: porsche38.url, top: "18%", left: "28%", rotate: -9, w: "14rem" },
  { src: porsche60.url, top: "48%", right: "18%", rotate: 8, w: "15rem" },
];

// Scattered across the full page (positions in % of total page height)
const fotoPageBgImages = [
  { src: harpen1.url, top: "8%", left: "-3%", rotate: -10, w: "18rem" },
  { src: angacom1.url, top: "15%", right: "-2%", rotate: 7, w: "19rem" },
  { src: porsche99.url, top: "22%", left: "6%", rotate: -7, w: "20rem" },
  { src: harpen2.url, top: "28%", right: "-2%", rotate: 8, w: "20rem" },
  { src: angacom8.url, top: "35%", left: "-3%", rotate: -8, w: "21rem" },
  { src: porsche80.url, top: "42%", right: "5%", rotate: 6, w: "18rem" },
  { src: harpen9.url, top: "46%", left: "2%", rotate: 6, w: "16rem" },
  { src: angacom26.url, top: "52%", right: "2%", rotate: 5, w: "17rem" },
  { src: porsche83.url, top: "58%", left: "8%", rotate: -5, w: "17rem" },
  { src: harpen4.url, top: "62%", right: "3%", rotate: -9, w: "19rem" },
  { src: angacom23.url, top: "70%", left: "-2%", rotate: -6, w: "18rem" },
  { src: porsche56.url, top: "76%", right: "7%", rotate: 7, w: "19rem" },
  { src: harpen10.url, top: "80%", left: "-2%", rotate: 5, w: "22rem" },
  { src: porsche63.url, top: "88%", left: "10%", rotate: -8, w: "18rem" },
  { src: harpen1.url, top: "92%", right: "-3%", rotate: -6, w: "17rem" },
];

const LeistungDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const section = slug ? getSectionBySlug(slug) : undefined;

  if (!section) return <Navigate to="/leistungen" replace />;

  const others = sections.filter((s) => s.slug !== section.slug);
  const Icon = section.icon;
  const isFoto = section.slug === "fotoproduktion";
  const isVideo = section.slug === "videoproduktion";
  const isCorporate = section.slug === "corporate-identity";
  const isWeb = section.slug === "webseiten-erstellung" || section.slug === "webseiten";
  const isSocial = section.slug === "social-media-marketing";
  const isSpecial = isFoto || isVideo || isCorporate || isWeb || isSocial;

  type SpecialPkg = { name: string; description: string; includes?: string[] };
  type SpecialContent = {
    title: string;
    tagline: string;
    intro: string;
    usage: string;
    offerHeading: string;
    offers: { icon: typeof Users; title: string; text: string }[];
    whyHeading: string;
    whySub: string;
    whyItems: string[];
    processHeading: string;
    processItems: { num: string; title: string; desc: string }[];
    packagesHeading?: string;
    packages?: SpecialPkg[];
  };

  const specialContent: SpecialContent = isVideo
    ? {
        title: "Videoproduktion",
        tagline: "Bewegtbild, das Aufmerksamkeit erzeugt und Geschichten erzählt.",
        intro:
          "Von Imagefilmen über Werbespots bis hin zu Eventdokumentationen produzieren wir hochwertige Videos, die Marken sichtbar machen, Emotionen transportieren und nachhaltig im Gedächtnis bleiben.",
        usage: "Für Website, Social Media, Werbung & Events.",
        offerHeading: "Professionelle Videolösungen für Unternehmen & Events",
        offers: [
          { icon: Film, title: "Image- & Markenfilme", text: "Authentische Unternehmensfilme, die Werte, Menschen und Leistungen sichtbar machen." },
          { icon: Megaphone, title: "Werbevideos & Social Media", text: "Kurzformate für Kampagnen, Reels, Shorts und digitale Werbung." },
          { icon: Calendar, title: "Eventbegleitung", text: "Emotionale Dokumentation von Firmenevents, Messen, Konferenzen und Veranstaltungen." },
          { icon: Plane, title: "Luftaufnahmen & Cinematic Content", text: "Dynamische Perspektiven für beeindruckende Marken- und Eventinszenierungen." },
        ],
        whyHeading: "Warum professionelle Videos?",
        whySub: "Bewegte Bilder schaffen Wirkung",
        whyItems: [
          "Mehr Aufmerksamkeit für Ihre Marke",
          "Professioneller Unternehmensauftritt",
          "Emotionale Kundenansprache",
          "Höhere Reichweite auf Social Media",
          "Stärkere Markenwahrnehmung",
          "Wiederverwendbarer Content für alle Plattformen",
          "Mehr Engagement & Interaktionen",
          "Langfristig nutzbares Videomaterial",
        ],
        processHeading: "Von der Idee bis zum fertigen Film",
        processItems: [
          { num: "01", title: "Konzeption", desc: "Entwicklung von Story, Botschaft und visueller Ausrichtung." },
          { num: "02", title: "Produktion", desc: "Professionelle Videoaufnahmen mit modernster Technik." },
          { num: "03", title: "Postproduktion", desc: "Schnitt, Color Grading, Sounddesign und Animation." },
          { num: "04", title: "Veröffentlichung", desc: "Optimierte Formate für Website, Social Media und Werbeanzeigen." },
        ],
      }
    : isCorporate
    ? {
        title: "Corporate Identity",
        tagline: "Marken, die im Gedächtnis bleiben.",
        intro:
          "Wir entwickeln ganzheitliche Markenauftritte mit klarer Strategie, einheitlicher Gestaltung & hohem Wiedererkennungswert – vom Logo bis zur vollständigen Geschäftsausstattung.",
        usage: "Für Unternehmen, die professionell auftreten & nachhaltig wahrgenommen werden möchten.",
        offerHeading: "Ein starker Markenauftritt aus einer Hand",
        offers: [
          { icon: Palette, title: "Logo & Markenentwicklung", text: "Ein individuelles Erscheinungsbild, das Ihre Werte sichtbar macht & Ihre Marke unverwechselbar positioniert." },
          { icon: FileText, title: "Geschäftsausstattung", text: "Professionell gestaltete Visitenkarten, Briefpapier, Präsentationen & weitere Geschäftsmaterialien." },
          { icon: BookOpen, title: "Designsystem & Richtlinien", text: "Klare Vorgaben für Farben, Schriften & Anwendungen sorgen für einen konsistenten Markenauftritt." },
          { icon: Globe, title: "Digitale Markenkommunikation", text: "Designvorlagen & Assets für Website, Social Media & digitale Werbemittel." },
        ],
        whyHeading: "Warum eine starke Corporate Identity?",
        whySub: "Ein professioneller Auftritt schafft Vertrauen",
        whyItems: [
          "Professioneller & vertrauenswürdiger Unternehmensauftritt",
          "Höherer Wiedererkennungswert Ihrer Marke",
          "Einheitliche visuelle Markenkommunikation",
          "Klare Positionierung im Wettbewerbsumfeld",
          "Konsistente Wahrnehmung über alle Kanäle",
          "Stärkere Bindung zur Zielgruppe",
          "Professionelle Darstellung in Print & Digital",
          "Nachhaltige Grundlage für zukünftiges Marketing",
        ],
        processHeading: "Unser Ablauf",
        processItems: [
          { num: "01", title: "Analyse", desc: "Wir definieren Werte, Zielgruppe & Positionierung Ihrer Marke." },
          { num: "02", title: "Konzept", desc: "Entwicklung der visuellen Identität – Logo, Farben & Typografie." },
          { num: "03", title: "Design", desc: "Gestaltung aller Markenelemente & Geschäftsausstattung." },
          { num: "04", title: "Übergabe", desc: "Bereitstellung aller Dateien inkl. Brand Guidelines." },
        ],
        packagesHeading: "Unsere Pakete",
        packages: [
          { name: "Starter", description: "Für Gründer & kleine Unternehmen.", includes: ["Logo-Design (2 Entwürfe)", "Farbpalette & Typografie", "Logo-Dateien in allen Formaten"] },
          { name: "Professional", description: "Für Unternehmen, die ihren Markenauftritt gezielt weiterentwickeln möchten.", includes: ["Alles aus Starter", "Gestaltung & Druckabwicklung von Druckprodukten", "Brand Strategie", "Brand Guidelines"] },
          { name: "Signature", description: "Die komplette Markenstrategie inklusive Corporate Design, Geschäftsausstattung & Brand Guidelines.", includes: ["Alles aus Professional", "Komplette Geschäftsausstattung", "Verpackungsdesign", "Social-Media-Templates", "Brand Book"] },
        ],
      }
    : isWeb
    ? {
        title: "Webseiten-Erstellung",
        tagline: "Websites, die überzeugen & verkaufen.",
        intro:
          "Wir entwickeln moderne Websites mit klarem Design, schneller Performance & strategischer Nutzerführung – damit Besucher zu Kunden werden.",
        usage: "Von der Unternehmenswebsite bis zum Online-Shop.",
        offerHeading: "Digitale Lösungen für nachhaltiges Wachstum",
        offers: [
          { icon: Monitor, title: "Unternehmenswebsites", text: "Professionelle Websites, die Ihr Unternehmen optimal präsentieren & Vertrauen schaffen." },
          { icon: FileText, title: "Landingpages & Onepager", text: "Kompakte Webseiten mit klarem Fokus auf Anfragen, Leads & Conversion." },
          { icon: ShoppingCart, title: "Online-Shops", text: "Verkaufsstarke E-Commerce-Lösungen mit einfacher Verwaltung & optimaler Nutzererfahrung." },
          { icon: Settings, title: "Content Management", text: "Inhalte selbst pflegen & flexibel erweitern – ohne technische Vorkenntnisse." },
        ],
        whyHeading: "Warum eine professionelle Website?",
        whySub: "Ihre Website arbeitet rund um die Uhr für Ihr Unternehmen",
        whyItems: [
          "Professioneller & vertrauenswürdiger Online-Auftritt",
          "Mehr qualifizierte Anfragen über Ihre Website",
          "Bessere Sichtbarkeit in Suchmaschinen",
          "Optimale Darstellung auf allen Endgeräten",
          "Schnelle Ladezeiten für ein besseres Nutzererlebnis",
          "Klare Nutzerführung mit Fokus auf Conversion",
          "Flexibel erweiterbar & einfach verwaltbar",
          "Nachhaltige Grundlage für digitales Wachstum",
        ],
        processHeading: "Unser Ablauf",
        processItems: [
          { num: "01", title: "Konzept", desc: "Analyse von Zielgruppe, Struktur & Inhalten Ihrer Website." },
          { num: "02", title: "Design", desc: "Modernes, individuelles Design abgestimmt auf Ihre Marke." },
          { num: "03", title: "Entwicklung", desc: "Technische Umsetzung mit Fokus auf Performance & SEO." },
          { num: "04", title: "Launch", desc: "Veröffentlichung, Tests & Übergabe inkl. Einweisung." },
        ],
        packagesHeading: "Unsere Pakete",
        packages: [
          { name: "Essential", description: "Für Gründer, Selbstständige & kleine Unternehmen.", includes: ["Einzelseite mit durchdachter Seitenstruktur", "Responsive Design für alle Endgeräte", "SEO-Grundoptimierung für lokale Sichtbarkeit", "Kontaktmöglichkeit & rechtssichere Pflichtseiten"] },
          { name: "Professional", description: "Für Unternehmen, die online professionell auftreten & wachsen möchten.", includes: ["Bis zu 7 Unterseiten (erweiterbar)", "Content-Management-System zum selbst pflegen", "SEO-Optimierung für organische Reichweite", "Performance-Optimierung für schnelle Ladezeiten"] },
          { name: "Performance", description: "Für Unternehmen, die Produkte oder Dienstleistungen digital verkaufen.", includes: ["Individuelles Shop-Design mit Fokus auf Nutzerführung", "Produktverwaltung & Zahlungsabwicklung", "Mobile Optimierung mit Conversion-Fokus", "SEO & GEO Performance", "3 Monate technischer Support nach Launch"] },
        ],
      }
    : isSocial
    ? {
        title: "Social Media Marketing",
        tagline: "Mehr Sichtbarkeit. Mehr Reichweite. Mehr Kunden.",
        intro:
          "Wir entwickeln Social-Media-Strategien, produzieren hochwertigen Content & betreuen Ihre Kanäle professionell – für nachhaltiges Wachstum & eine starke Markenpräsenz.",
        usage: "Von der Content-Erstellung bis zum vollständigen Account-Management.",
        offerHeading: "Erfolgreiche Social-Media-Lösungen aus einer Hand",
        offers: [
          { icon: Target, title: "Strategie & Positionierung", text: "Klare Konzepte, die Ihre Zielgruppe erreichen & Ihre Marke nachhaltig stärken." },
          { icon: Sparkles, title: "Content-Erstellung", text: "Professionelle Foto-, Video- & Grafikproduktionen für moderne Social-Media-Kanäle." },
          { icon: MessageCircle, title: "Community Management", text: "Betreuung Ihrer Kanäle, Interaktion mit Ihrer Community & aktive Markenpflege." },
          { icon: BarChart3, title: "Analyse & Optimierung", text: "Datengestützte Auswertungen zur kontinuierlichen Verbesserung Ihrer Ergebnisse." },
        ],
        whyHeading: "Warum Social Media Marketing?",
        whySub: "Dort präsent sein, wo Ihre Zielgruppe täglich unterwegs ist",
        whyItems: [
          "Mehr Sichtbarkeit für Ihre Marke",
          "Höhere Reichweite in relevanten Zielgruppen",
          "Professioneller & konsistenter Markenauftritt",
          "Hochwertiger Content mit Wiedererkennungswert",
          "Aktive Community & stärkere Kundenbindung",
          "Mehr Aufmerksamkeit für Produkte & Dienstleistungen",
          "Mehr qualifizierte Anfragen über Social Media",
          "Nachhaltiges Wachstum Ihrer Online-Präsenz",
        ],
        processHeading: "Unser Ablauf",
        processItems: [
          { num: "01", title: "Analyse & Strategie", desc: "Wir analysieren Ihre Zielgruppe, Konkurrenz & entwickeln eine maßgeschneiderte Social-Media-Strategie." },
          { num: "02", title: "Content-Produktion", desc: "Professionelle Erstellung von Foto-, Video- & Grafikcontent passend zu Ihrer Marke & Zielgruppe." },
          { num: "03", title: "Publishing & Community", desc: "Planung, Veröffentlichung & aktives Community-Management für beständiges Wachstum." },
          { num: "04", title: "Analyse & Optimierung", desc: "Datengestützte Auswertungen zur kontinuierlichen Steigerung von Reichweite & Engagement." },
        ],
        packagesHeading: "Unsere Pakete",
        packages: [
          { name: "Essential", description: "Für Unternehmen, die professionell starten möchten.", includes: ["Content-Plan & Strategie", "Account- & Community-Management", "Regelmäßige Postings (Basis-Frequenz)", "Monatliches Reporting & Analyse"] },
          { name: "Growth", description: "Für Marken, die Reichweite & Community aktiv ausbauen wollen.", includes: ["Content-Plan & Strategie", "Account- & Community-Management", "Erhöhte Posting-Frequenz", "Monatliches Reporting & Analyse", "Reichweiten-Optimierung"] },
          { name: "Performance", description: "Für Unternehmen mit Fokus auf Wachstum, Sichtbarkeit & kontinuierliche Optimierung.", includes: ["Content-Strategie & Planung", "Account- & Community-Management", "Erhöhte Posting-Frequenz", "Alle Plattformen", "Monatliches Reporting & Analyse", "Tägliches Monitoring & Optimierung"] },
        ],
      }
    : {
        title: "Fotoproduktion",
        tagline: "Bilder, die Marken sichtbar machen.",
        intro:
          "Von Business-Portraits über Produktfotografie bis hin zu Immobilien- und Interieuraufnahmen erstellen wir Bildmaterial, das Vertrauen schafft, Professionalität vermittelt und Ihre Marke authentisch präsentiert.",
        usage: "Für Website, Social Media, Print und Werbung.",
        offerHeading: "Professionelle Bildwelten für Ihr Unternehmen",
        offers: [
          { icon: Users, title: "Business & Portraits", text: "Authentische Mitarbeiter- und Unternehmensportraits für Website, LinkedIn und Marketing." },
          { icon: Camera, title: "Produktfotografie", text: "Hochwertige Produktbilder für Onlineshops, Kataloge und Werbekampagnen." },
          { icon: Home, title: "Immobilien & Interieur", text: "Moderne Aufnahmen von Innen- und Außenbereichen für Vermarktung und Präsentation." },
          { icon: Share2, title: "Social-Media-Content", text: "Bildmaterial im passenden Format für Instagram, LinkedIn, Facebook und Werbeanzeigen." },
        ],
        whyHeading: "Warum professionelle Fotos?",
        whySub: "Der erste Eindruck entscheidet",
        whyItems: [
          "Mehr Vertrauen bei Kunden",
          "Professioneller Markenauftritt",
          "Einheitlicher Markenauftritt",
          "Wiedererkennbare Bildsprache",
          "Mehr Aufmerksamkeit auf Website & Social Media",
          "Authentische Darstellung Ihres Unternehmens",
          "Hochwertiger Content für alle Kanäle",
          "Langfristig nutzbares Bildmaterial",
        ],
        processHeading: "Unser Ablauf",
        processItems: [
          { num: "01", title: "Konzept", desc: "Wir definieren gemeinsam Stil, Bildsprache und Ziele." },
          { num: "02", title: "Produktion", desc: "Professionelles Fotoshooting vor Ort oder im Studio." },
          { num: "03", title: "Bearbeitung", desc: "Auswahl und hochwertige Nachbearbeitung der besten Aufnahmen." },
          { num: "04", title: "Übergabe", desc: "Optimierte Dateien für Web, Social Media und Print." },
        ],
      };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SEO title={section.seoTitle} description={section.seoDescription} path={`/leistungen/${section.slug}`} />
      <Header />

      {isSpecial ? (
        <div className="absolute inset-0 pointer-events-none z-0" aria-hidden="true">
          {fotoPageBgImages.map((img, i) => (
            <img
              key={`page-${i}`}
              src={img.src}
              alt=""
              loading="lazy"
              className="absolute rounded-xl shadow-2xl ring-1 ring-white/10 opacity-30"
              style={{
                top: img.top,
                left: img.left,
                right: img.right,
                width: img.w,
                transform: `rotate(${img.rotate}deg)`,
              }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/80 to-background/95" />
        </div>
      ) : (
        <PageBackground />
      )}


      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
        {isSpecial && (
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            {fotoBgImages.map((img, i) => (
              <img
                key={i}
                src={img.src}
                alt=""
                loading="lazy"
                className="absolute rounded-xl shadow-2xl ring-1 ring-white/10 opacity-25 hover:opacity-40 transition-opacity duration-500"
                style={{
                  top: img.top,
                  left: img.left,
                  right: img.right,
                  width: img.w,
                  transform: `rotate(${img.rotate}deg)`,
                }}
              />
            ))}
            <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/60 to-background/90" />
          </div>
        )}
        <div className="container mx-auto px-4 relative z-10">
          <ScrollReveal>
            <Link to="/leistungen" className="text-primary/80 hover:text-primary text-sm tracking-wider uppercase">
              ← Alle Leistungen
            </Link>
            {isSpecial ? (
              <>
                <h1 className="text-5xl md:text-7xl font-bold text-foreground mt-6 mb-4">{specialContent.title}</h1>
                <p className="text-2xl md:text-3xl font-semibold text-foreground/90 mb-6">{specialContent.tagline}</p>
                <p className="text-xl text-foreground/85 max-w-3xl mb-4">
                  {specialContent.intro}
                </p>
                <p className="text-lg text-foreground/70">{specialContent.usage}</p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-4 mt-6 mb-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Icon size={28} className="text-primary" />
                  </div>
                  <span className="text-primary text-sm font-medium tracking-wider uppercase">Leistung</span>
                </div>
                <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6">{section.title}</h1>
                <p className="text-xl text-foreground/85 max-w-2xl">{section.longDescription}</p>
              </>
            )}
          </ScrollReveal>
        </div>
      </section>

      {/* Items & Packages - grouped if groups are defined */}
      {isSpecial ? (
        <>
          {/* Was Sie erhalten */}
          <section className="py-16 relative z-10">
            <div className="container mx-auto px-4">
              <ScrollReveal>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{specialContent.offerHeading}</h2>
              </ScrollReveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
                {specialContent.offers.map((offer, i) => {
                  const OfferIcon = offer.icon;
                  return (
                    <ScrollReveal key={offer.title} delay={i * 100}>
                      <div className="p-6 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition-all duration-300 h-full">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                          <OfferIcon size={22} className="text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground mb-3">{offer.title}</h3>
                        <p className="text-foreground/85 text-base leading-relaxed">{offer.text}</p>
                      </div>
                    </ScrollReveal>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Warum professionelle Fotos/Videos? */}
          <section className="py-16 bg-card/30 relative z-10">
            <div className="container mx-auto px-4">
              <ScrollReveal>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">{specialContent.whyHeading}</h2>
                <p className="text-xl text-foreground/85 mb-12">{specialContent.whySub}</p>
              </ScrollReveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {specialContent.whyItems.map((text, i) => (
                  <ScrollReveal key={text} delay={i * 100}>
                    <div className="flex items-center gap-3 p-4 rounded-xl bg-gradient-card border border-border/50">
                      <CheckCircle2 size={20} className="text-primary shrink-0" />
                      <span className="text-foreground/85 text-base font-medium">{text}</span>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {/* Unser Ablauf */}
          <section className="py-16 relative z-10">
            <div className="container mx-auto px-4">
              <ScrollReveal>
                <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">{specialContent.processHeading}</h2>
              </ScrollReveal>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {specialContent.processItems.map((step, i) => (
                  <ScrollReveal key={step.num} delay={i * 100}>
                    <div className="p-6 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition-all duration-300 h-full">
                      <span className="text-primary font-bold text-2xl mb-3 block" style={{ fontFamily: '"agency-fb", sans-serif', fontWeight: 900, fontStyle: 'italic' }}>{step.num}</span>
                      <h3 className="text-xl font-semibold text-foreground mb-3">{step.title}</h3>
                      <p className="text-foreground/85 text-base leading-relaxed">{step.desc}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </section>

          {specialContent.packages && (
            <section className="py-16 bg-card/30 relative z-10">
              <div className="container mx-auto px-4">
                <ScrollReveal>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">{specialContent.packagesHeading ?? "Unsere Pakete"}</h2>
                </ScrollReveal>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {specialContent.packages.map((pkg, pIdx) => (
                    <ScrollReveal key={pkg.name} delay={pIdx * 100}>
                      <div className="p-6 rounded-2xl border border-border/50 bg-gradient-card hover:border-primary/20 transition-all duration-300 flex flex-col h-full">
                        <div className="flex flex-col items-start gap-1 mb-3">
                          <span className="text-primary font-bold text-base">{String(pIdx + 1).padStart(2, "0")}</span>
                          <span className="font-semibold text-xl text-foreground" style={{ fontFamily: '"agency-fb", sans-serif', fontWeight: 900, fontStyle: "italic", textTransform: "uppercase" }}>{pkg.name}</span>
                        </div>
                        <p className="text-foreground/85 text-base mb-5 whitespace-pre-line">{pkg.description}</p>
                        {pkg.includes && (
                          <ul className="space-y-2 mt-auto">
                            {pkg.includes.map((inc) => (
                              <li key={inc} className="flex items-center gap-2 text-base text-foreground/85">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                {inc}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </ScrollReveal>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      ) : section.groups ? (
        section.groups.map((group, gIdx) => {
          const item = section.items[group.itemIndex];
          const pkg = section.packages[group.packageIndex];
          if (!item || !pkg) return null;
          return (
            <section key={group.title} className={`py-24 relative z-10 ${gIdx % 2 === 1 ? "bg-card/30" : ""}`}>
              <div className="container mx-auto px-4">
                <ScrollReveal>
                  <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">{group.title}</h2>
                </ScrollReveal>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Item */}
                  <ScrollReveal delay={100}>
                    <div className="p-8 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition-all duration-300 h-full flex flex-col">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                        <item.icon size={22} className="text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                      <p className="text-foreground/85 text-base leading-relaxed mb-6">{item.text}</p>
                      {group.projectFilter && (
                        <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground rounded-full px-8 h-12 mt-auto w-fit">
                          <Link to={`/projekte?filter=${group.projectFilter}`}>
                            Zu den Projekten <ArrowRight className="ml-2" size={18} />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </ScrollReveal>
                  {/* Package */}
                  <ScrollReveal delay={200}>
                    <div className="p-6 rounded-2xl border border-border/50 bg-gradient-card hover:border-primary/20 transition-all duration-300 flex flex-col h-full">
                      <span className="font-semibold text-xl text-foreground mb-3" style={{ fontFamily: '"agency-fb", sans-serif', fontWeight: 900, fontStyle: "italic", textTransform: "uppercase" }}>{pkg.name}</span>
                      <p className="text-foreground/85 text-base mb-5 whitespace-pre-line">{pkg.description}</p>
                      <ul className="space-y-2 mt-auto">
                        {pkg.includes.map((inc) => (
                          <li key={inc} className="flex items-center gap-2 text-base text-foreground/85">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                            {inc}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </ScrollReveal>
                </div>
              </div>
            </section>
          );
        })
      ) : (
        <>
          {/* Items + Packages */}
          <section className="py-24 relative z-10">
            <div className="container mx-auto px-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* Was wir liefern */}
                <div>
                  <ScrollReveal>
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">Was wir liefern</h2>
                  </ScrollReveal>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {section.items.map((item, i) => (
                      <ScrollReveal key={item.title} delay={i * 100}>
                        <div className="p-8 rounded-2xl bg-gradient-card border border-border/50 hover:border-primary/20 transition-all duration-300 h-full">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5">
                            <item.icon size={22} className="text-primary" />
                          </div>
                          <h3 className="text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                          <p className="text-foreground/85 text-base leading-relaxed">{item.text}</p>
                        </div>
                      </ScrollReveal>
                    ))}
                  </div>
                </div>

                {/* Unser Service */}
                <div>
                  <ScrollReveal>
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12">Unser Service</h2>
                  </ScrollReveal>
                  <div className="grid grid-cols-1 gap-6">
                    {section.packages.map((pkg, pIdx) => (
                      <ScrollReveal key={pkg.name} delay={pIdx * 100}>
                        <div className="p-6 rounded-2xl border border-border/50 bg-gradient-card hover:border-primary/20 transition-all duration-300 flex flex-col h-full">
                          <div className="flex flex-col items-start gap-1 mb-3">
                            <span className="text-primary font-bold text-base">{String(pIdx + 1).padStart(2, "0")}</span>
                            <span className="font-semibold text-xl text-foreground" style={{ fontFamily: '"agency-fb", sans-serif', fontWeight: 900, fontStyle: "italic", textTransform: "uppercase" }}>{pkg.name}</span>
                          </div>
                          <p className="text-foreground/85 text-base mb-5 whitespace-pre-line">{pkg.description}</p>
                          <ul className="space-y-2 mt-auto">
                            {pkg.includes.map((inc) => (
                              <li key={inc} className="flex items-center gap-2 text-base text-foreground/85">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                                {inc}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </ScrollReveal>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>

      )}

      {/* CTA */}
      <section className="py-24 relative z-10">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
              Bereit für dein <span className="text-gradient">{section.title}</span>-Projekt?
            </h2>
            <p className="text-foreground/85 max-w-xl mx-auto mb-10">Erzähl uns von deinem Vorhaben & wir melden uns mit einem passenden Vorschlag.</p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/80 text-primary-foreground rounded-full px-10 h-14">
              <Link to="/kontakt">Projekt anfragen <ArrowRight className="ml-2" size={20} /></Link>
            </Button>
          </ScrollReveal>
        </div>
      </section>


      <div className="relative z-10">
        <Footer />
      </div>
    </div>
  );
};

export default LeistungDetail;
